
import { PARegistry } from "./pa-registry";
import { PATechnician } from "../classes/technician";
import type { WeekDay } from "../classes/util";
import { PAUtil } from "../classes/util";
import { PAFilter } from "../classes/filter";
import { PAProject } from "../classes/project";
import type {  UserService } from "../../../_services";
import type { PAOperation } from "../classes/operation";

export class PAFilterControl {

  private static _instance: PAFilterControl

  public static get Instance()
  {
    return this._instance || (this._instance = new this());
  }

  /** Global ausgewählte Projekte — früher static auf PAProject; Selektion ist Filter-Zustand
   *  und der Static bildete einen Wert-Import-Zyklus store → project */
  public selectedProjects: PAProject[] = []

  // Technicians
  globalFilteredTechnicians: PATechnician[] = []
  preferredCalendarTechnicians: PATechnician[] = []
  selectedTechnicianOwnerFilter: string = "off"
  selectedTechnicianOperationsInCalendarWeekRangeFilter = "off"
  /** Vorbelegung des Kalenderwochen-Filters: die laufende Woche.
   *
   *  Bewusst LAZY statt als Feld-Initialisierer. Ein Feld-Initialisierer greift
   *  beim ersten `.Instance` quer auf ein anderes Singleton zu, und ob dessen
   *  Modul zu diesem Zeitpunkt schon fertig ausgewertet ist, entscheidet die
   *  Modulreihenfolge des Bundles. In der CI-Pipeline lief `PARegistry.timeControl`
   *  dadurch in ein halb initialisiertes Objekt ("getCurrentCalendarWeek is not a
   *  function") — lokal nicht reproduzierbar, weil dort eine andere Reihenfolge
   *  gewinnt. Die Getter verschieben den Zugriff auf den ersten echten Lesezugriff,
   *  und der passiert erst, wenn der Filter tatsächlich benutzt wird. */
  private _calendarWeekNumber?: number
  private _calendarWeekYear?: number

  get operationsInCalendarWeekRangeCalendarWeekNumber(): number {
    return this._calendarWeekNumber ??= PARegistry.timeControl.getCurrentCalendarWeek().number
  }

  set operationsInCalendarWeekRangeCalendarWeekNumber(value: number) {
    this._calendarWeekNumber = value
  }

  get operationsInCalendarWeekRangeCalendarWeekYear(): number {
    return this._calendarWeekYear ??= PARegistry.timeControl.getCurrentCalendarWeek().year
  }

  set operationsInCalendarWeekRangeCalendarWeekYear(value: number) {
    this._calendarWeekYear = value
  }
  queuedTechnicianFilterUpdate = false
  runningTechnicianFilterUpdate = false

  // Operations
  public globalFilteredOperations: PAOperation[] = []
  public selectedOperationTimeFilter: 'planned' | 'average' = "planned"
  public queuedOperationFilterUpdate = false
  public runningOperationFilterUpdate = false
  public operationHasMaterialFilter = 'off'
  public operationHasDeadlineFilter = 'off'
  public unplannedOperations: PAOperation[] = []
  public filteredUnplannedOperations: PAOperation[] = []

  // Projects
  projectSearchString = ''
  nonServiceProjects: PAProject[] = []
  serviceProjects: PAProject[] = []
  filteredNonServiceProjects: PAProject[] = []
  filteredServiceProjects: PAProject[] = []
  serviceProjectsToggleGroup: string = ''
  nonServiceProjectsToggleGroup: string = ''

  private _userService: UserService

  // WeekDays
  filteredWeekDays: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

  private constructor(
  ) {
  }

  set userService(us: UserService) {
    this._userService = us
  }

  public async updateTechnicianFilter() {
    if (this.queuedTechnicianFilterUpdate) {
      return
    }
    if (this.runningTechnicianFilterUpdate) {
      this.queuedTechnicianFilterUpdate = true
      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return this.runningTechnicianFilterUpdate
        }
      )
      this.queuedTechnicianFilterUpdate = false
    }
    this.runningTechnicianFilterUpdate = true
    this.globalFilteredTechnicians = await this.filterTechnicians()

    this.runningTechnicianFilterUpdate = false

  }

  public async filterTechnicians(): Promise<PATechnician[]> {

    let technicians = PARegistry.dataControl.loadedTechnicians

    // Filter out pseudo technicians
    technicians = PATechnician.filterOutPseudoTechnicians(technicians)

    // Technician Owner Filter
    if (this.selectedTechnicianOwnerFilter == 'bentomax') {
      technicians = PAFilter.filterTechniciansForBentomaxTechnicians(technicians)
    } else if (this.selectedTechnicianOwnerFilter == 'external') {
      technicians = PAFilter.filterTechniciansForExternalTechnicians(technicians)
    }

    // Project Membership Filter
    if (this.selectedProjects.length) {
      technicians = PAFilter.filterTechniciansForProjectsInclusiveTechnicians(technicians, this.selectedProjects.map(project => project.id))
    }

    // Has Operations In Calendar Week Range Filter
    if (this.selectedTechnicianOperationsInCalendarWeekRangeFilter == 'technicians_with_operations') {
      technicians = PAFilter.filterTechniciansForOperations(technicians)
    } else if (this.selectedTechnicianOperationsInCalendarWeekRangeFilter == 'technicians_with_operations_in_calendar_week' && this.operationsInCalendarWeekRangeCalendarWeekNumber && this.operationsInCalendarWeekRangeCalendarWeekYear) {
      technicians = await PAFilter.filterTechniciansForOperationsInCalendarWeekRange(technicians, [{
        number: this.operationsInCalendarWeekRangeCalendarWeekNumber,
        year: this.operationsInCalendarWeekRangeCalendarWeekYear
      }], this._userService)
    } else if (this.selectedTechnicianOperationsInCalendarWeekRangeFilter == 'technicians_without_operations_in_calendar_week' && this.operationsInCalendarWeekRangeCalendarWeekNumber && this.operationsInCalendarWeekRangeCalendarWeekYear) {
      technicians = await PAFilter.filterTechniciansForAbsentOperationsInCalendarWeekRange(technicians, [{
        number: this.operationsInCalendarWeekRangeCalendarWeekNumber,
        year: this.operationsInCalendarWeekRangeCalendarWeekYear
      }], this._userService)
    }

    // Show Bentomax Technicians first
    technicians.sort(
      (technician_1, technician_2) => (
        technician_1.isBentomaxTechnician() <
        technician_2.isBentomaxTechnician()
      ) ? 1 : -1
    )
    return technicians
  }

  getTechnicianFilterString(): string {

    let owner_string = ''
    switch (this.selectedTechnicianOwnerFilter) {
      case 'bentomax': {
        owner_string = 'Bentomax Techniker'
        break
      }
      case 'external': {
        owner_string = 'Externe Techniker'
        break
      }
    }
    let has_operations_string = ''
    switch (this.selectedTechnicianOperationsInCalendarWeekRangeFilter) {
      case 'technicians_with_operations_in_calendar_week': {
        has_operations_string = `mit Aufträgen in KW ${this.operationsInCalendarWeekRangeCalendarWeekNumber} ${this.operationsInCalendarWeekRangeCalendarWeekYear}`
        break
      }
      case 'technicians_without_operations_in_calendar_week': {
        has_operations_string = `ohne Aufträge in KW ${this.operationsInCalendarWeekRangeCalendarWeekNumber} ${this.operationsInCalendarWeekRangeCalendarWeekYear}`
        break
      }
    }
    if (owner_string && has_operations_string) {
      return [owner_string, has_operations_string].join(' ')
    } else if (owner_string) {
      return owner_string
    } else if (has_operations_string) {
      return 'Techniker ' + has_operations_string
    } else {
      return 'Alle Techniker'
    }
  }

  public async updateOperationFilter(wait_until_all_updates_finished?: boolean) {
    if (this.queuedOperationFilterUpdate) {
      if (wait_until_all_updates_finished) {
        await PAUtil.sleepUntilConditionBreaks(
          () => {
            return this.runningOperationFilterUpdate
          }
        )
      }
      return
    }

    if (this.runningOperationFilterUpdate) {
      this.queuedOperationFilterUpdate = true
      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return this.runningOperationFilterUpdate
        }
      )
      this.queuedOperationFilterUpdate = false
    }

    this.runningOperationFilterUpdate = true
    this.globalFilteredOperations = this.filterOperations()

    this.updateFilteredUnplannedOperations()

    this.runningOperationFilterUpdate = false

  }

  public filterOperations(): PAOperation[] {

    let operations = PARegistry.dataControl.getChangedLoadedOperations()

    // Project Membership Filter
    operations = PAFilter.filterOperationsForProjectsInclusiveOperations(operations, this.selectedProjects.map(project => project.id))

    if (this.operationHasMaterialFilter == 'true') {
      operations = PAFilter.filterOperationsForNeededMaterial(operations)
    } else if (this.operationHasMaterialFilter == 'false') {
      operations = PAFilter.filterOperationsForNoNeededMaterial(operations)
    }

    if (this.operationHasDeadlineFilter == 'sla_or_appointment') {
      operations = PAFilter.filterOperationsForSlaOrAppointment(operations)
    } else if (this.operationHasDeadlineFilter == 'sla') {
      operations = PAFilter.filterOperationsForSla(operations)
    } else if (this.operationHasDeadlineFilter == 'appointment') {
      operations = PAFilter.filterOperationsForAppointment(operations)
    }

    return operations
  }

  getOperationFilterString(): string {
    const active_filter = []
    switch (this.operationHasMaterialFilter) {
      case 'true': {
        active_filter.push('mit benötigtem Material')
        break
      }
      case 'false': {
        active_filter.push('ohne benötigtes Material')
        break
      }
    }
    switch (this.operationHasDeadlineFilter) {
      case 'sla_or_appointment': {
        active_filter.push('mit SLA/Termin')
        break
      }
      case 'sla': {
        active_filter.push('mit SLA')
        break
      }
      case 'appointment': {
        active_filter.push('mit Termin')
        break
      }
    }
    if (active_filter.length) {
      return `Aufträge ${active_filter.join(' und ')}`
    } else {
      return 'Alle Aufträge'
    }
  }

  public updateUnassignedOperations(): void {
    this.unplannedOperations = PARegistry.dataControl.getUnassignedOperations()
    this.updateFilteredUnplannedOperations()
  }

  public updateFilteredUnplannedOperations():void {
    this.filteredUnplannedOperations = this.unplannedOperations.filter(operation => this.globalFilteredOperations.includes(operation))
  }

  async toggleProjectSelection(project: PAProject): Promise<void> {
    const projects = this.selectedProjects
    if (projects.includes(project)) {
      this.selectedProjects = projects.filter(p => p != project)
    } else {
      this.selectedProjects = projects.concat([project])
    }
    this.updateProjectToggleGroups()
    await this.loadNotLoadedSelectedProjects()
  }

  updateShownProjects(): void {
    this.nonServiceProjects = PARegistry.dataControl.loadedProjects.filter(project => project.isNotServiceProject())
    this.serviceProjects = PARegistry.dataControl.loadedProjects.filter(project => project.isServiceProject())
    this.filteredNonServiceProjects = this.nonServiceProjects.filter(project => project.includesString(this.projectSearchString))
    this.filteredServiceProjects = this.serviceProjects.filter(project => project.includesString(this.projectSearchString))
  }

  selectAllServiceProjects(): void {
    this.selectedProjects = [...new Set(this.selectedProjects.concat(this.serviceProjects))]
    this.loadNotLoadedSelectedProjects()
  }

  deselectAllServiceProjects(): void {
    this.selectedProjects = this.selectedProjects.filter(project => !this.serviceProjects.includes(project))
    this.loadNotLoadedSelectedProjects()
  }

  selectAllNonServiceProjects(): void {
    this.selectedProjects = [...new Set(this.selectedProjects.concat(this.nonServiceProjects))]
    this.loadNotLoadedSelectedProjects()
  }

  deselectAllNonServiceProjects(): void {
    this.selectedProjects = this.selectedProjects.filter(project => !this.nonServiceProjects.includes(project))
    this.loadNotLoadedSelectedProjects()
  }

  updateProjectToggleGroups(): void {
    this.updateServiceProjectToggleGroups()
    this.updateNonServiceProjectToggleGroups()
  }

  updateServiceProjectToggleGroups(): void {
    switch (this.selectedProjects.filter(project => project.isServiceProject()).length) {
      case 0 : {
        this.serviceProjectsToggleGroup = 'none'
        break;
      }
      case PARegistry.dataControl.loadedProjects.filter(project => project.isServiceProject()).length : {
        this.serviceProjectsToggleGroup = 'all'
        break;
      }
      default: {
        this.serviceProjectsToggleGroup = 'selected'
        break;
      }
    }
  }

  updateNonServiceProjectToggleGroups(): void {
    switch (this.selectedProjects.filter(project => project.isNotServiceProject()).length) {
      case 0 : {
        this.nonServiceProjectsToggleGroup = 'none'
        break;
      }
      case PARegistry.dataControl.loadedProjects.filter(project => project.isNotServiceProject()).length : {
        this.nonServiceProjectsToggleGroup = 'all'
        break;
      }
      default: {
        this.nonServiceProjectsToggleGroup = 'selected'
        break;
      }
    }
  }

  async loadNotLoadedSelectedProjects() {
    const not_loaded_projects = this.selectedProjects.filter(project => project.operationLoadingStatus == 'init')
    if (not_loaded_projects.length) {
      const promises: Promise<void>[] = []
      for (const project of not_loaded_projects) {
        promises.push(project.loadUnplannedOperationsForProject())
        promises.push(project.loadUnplannedTicketsForProject())
      }
      await Promise.all(promises)
    }

    await this.updateTechnicianFilter()
    await this.updateOperationFilter()
  }

  internalProjectIsActivated() {
    return this.selectedProjects.includes(PARegistry.dataControl.getProject(133))
  }
}

// Registrierung für die späte Bindung (Zyklen-Entkopplung, siehe pa-registry.ts)
PARegistry.register('filterControl', () => PAFilterControl.Instance);
